import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { getComicsByCategory } from '@/mocks/comics';

interface CategoryGridProps {
  activeCategorySlug?: string;
}

const colorMap: Record<string, string> = {
  amber: 'bg-amber-100/60',
  mint: 'bg-emerald-100/60',
  rose: 'bg-rose-100/60',
  gray: 'bg-gray-100/60',
};

const textColorMap: Record<string, string> = {
  amber: 'text-amber-800',
  mint: 'text-emerald-800',
  rose: 'text-rose-800',
  gray: 'text-gray-800',
};

export default function CategoryGrid({ activeCategorySlug }: CategoryGridProps) {
  const displayCategories = activeCategorySlug
    ? categories.filter((c) => c.slug === activeCategorySlug)
    : categories;

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="w-full max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="mb-10 md:mb-14">
          <span className="text-xs font-medium text-foreground-500 tracking-widest">
            分类导航
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mt-3">
            探索
          </h2>
          <p className="font-heading text-lg md:text-2xl italic text-foreground-400 mt-1">
            发现
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6" data-product-shop="">
          {displayCategories.map((cat) => {
            const comics = getComicsByCategory(cat.slug);

            return (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`group rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 ${
                  activeCategorySlug === cat.slug ? 'ring-2 ring-accent-500' : 'hover:ring-2 hover:ring-accent-300'
                }`}
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={cat.image}
                    alt={`${cat.name}漫画分类`}
                    title={`${cat.name}高清漫画全集免费阅读下拉式 - 178漫画网`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  <span className="absolute bottom-3 left-3 text-white text-xs font-medium bg-black/30 px-2.5 py-1 rounded-full backdrop-blur-sm">
                    {cat.comicCount} 部作品
                  </span>
                </div>

                <div className="p-4 md:p-5 bg-white">
                  <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900">
                    {cat.name}
                  </h3>
                  <p className="text-sm text-foreground-500 mt-1.5 line-clamp-1">
                    {cat.description}
                  </p>

                  {comics.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {comics.slice(0, 3).map((c) => (
                        <span
                          key={c.id}
                          className={`text-xs px-2 py-1 rounded-md ${colorMap[cat.colorClass] || 'bg-background-100'} ${textColorMap[cat.colorClass] || 'text-foreground-700'}`}
                        >
                          {c.title}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Link>
            );
          })}
        </div>

        {activeCategorySlug && (
          <div className="mt-12">
            <h3 className="font-heading text-xl font-bold text-foreground-900 mb-6">
              该分类下的漫画作品
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6" data-product-shop="">
              {getComicsByCategory(activeCategorySlug).map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.slug}`}
                  className="group cursor-pointer"
                >
                  <div className="relative rounded-2xl overflow-hidden aspect-[3/4] mb-3 bg-background-200">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      title={`${comic.title}高清漫画全集免费阅读下拉式 - 178漫画网`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent">
                      <span className="text-white/80 text-xs">
                        ⭐ {comic.rating} · {comic.totalChapters}话
                      </span>
                    </div>
                  </div>
                  <h4 className="font-heading text-sm md:text-base font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                    {comic.title}
                  </h4>
                  <p className="text-xs text-foreground-500 mt-1">
                    {comic.author}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}